import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  Heart, ShoppingBag, SlidersHorizontal, Search, X, Check, Minus, Plus, ArrowRight, ChevronLeft, ChevronRight, PackageOpen,
  Home as HomeIcon, ClipboardList, User, LogOut, LayoutDashboard, Eye, Share2,
  Sparkles, Globe, Menu as MenuIcon,
} from "lucide-react";
import logo from "../../assets/app/logo.png";
import { RootState, AppDispatch } from "../../redux/store";
import { listenToPublishedProducts, Product } from "../../redux/productSlice";
import { logout } from "../../redux/authSlice";
/* ── CART ADAPTER: if your cartSlice uses other names, this import + the 3 helpers below are the only edits ── */
import { addToCart, decrementCartItem } from "../../redux/cartSlice";
import { AllCategoriesFlat, DEPARTMENTS, CategoryItem } from "./Categories";

const selectCartItems = (s: any): any[] => s.cart?.items ?? [];
const lineQty = (i: any): number => Number(i?.quantity ?? i?.qty ?? 1);
const lineId = (i: any): string => String(i?.productId ?? i?.listingId ?? "");

const CURRENCY_LABEL = "EUR";

/* ═══════════════════════════ CATEGORY HELPERS ═══════════════════════════ */
const FAV_KEY = "sportman_favs";

const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) => AllCategoriesFlat.filter((c) => c.parentId === parentId);
const idOf = (v: any): string | null => (v === undefined || v === null || v === "" ? null : String(v));

const chainCache = new Map<string, string[]>();
/** [department, category, type, subcategory] for any node id */
const chainIdsOf = (id?: string | null): string[] => {
  if (!id) return [];
  const hit = chainCache.get(id);
  if (hit) return hit;
  const chain: string[] = [];
  const seen = new Set<string>();
  let cur = CAT_MAP.get(id);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(id, chain);
  return chain;
};

const catName = (id?: string | null) => (id ? CAT_MAP.get(id)?.name ?? "" : "");
const catPathLabel = (id?: string | null) => chainIdsOf(id).slice(1).map(catName).filter(Boolean).join(" · ");
type AttrMap = Record<string, string | string[]>;
const attrsOf = (p: Product): AttrMap => ((p as any).attributes || {}) as AttrMap;
const attrText = (v: string | string[]) => (Array.isArray(v) ? v.join(", ") : String(v));
const TOP_SPECS = ["Brand", "Model", "Condition", "Warranty"];
/* spec rows for the detail panel: Brand, Model, Condition, Warranty first, Color excluded (it has its own picker) */
const specsOf = (p: Product): [string, string][] =>
  Object.entries(attrsOf(p))
    .filter(([k, v]) => k !== "Color" && v != null && attrText(v).trim() !== "")
    .sort(([a], [b]) => {
      const ia = TOP_SPECS.indexOf(a), ib = TOP_SPECS.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    })
    .map(([k, v]) => [k, attrText(v)]);

const money = (v: number) => `€${(Number(v) || 0).toFixed(2)}`;
const isLive = (p: Product) => (p as any).published !== false;
const stockOf = (p: Product) => Math.max(0, Number((p as any).quantity) || 0);

type Variant = { color: string; hex?: string | null; images?: string[]; stock?: { size: string; qty: number }[] };
type PickedOpt = { color?: string; size?: string };
const variantsOf = (p: Product): Variant[] => (((p as any).variants as Variant[]) || []).filter((v) => v && v.color);
const variantQty = (v: Variant) => (v.stock || []).reduce((s, x) => s + (Number(x.qty) || 0), 0);
const coverOf = (p: Product): string | undefined =>
  (p as any).images?.[0] || variantsOf(p).flatMap((v) => v.images || [])[0];

const SORTS = [
  { id: "new", label: "Newest" },
  { id: "low", label: "Price: low to high" },
  { id: "high", label: "Price: high to low" },
  { id: "name", label: "Name A-Z" },
] as const;
type Sort = (typeof SORTS)[number]["id"];
type Feed = "home" | "new";

/* ═══════════════════════════ PALETTE: black, white, hover-yellow ═══════════════════════════ */
const INK = "#000";
const RED = "#E10000";
const RED_DARK = "#B30000";
const BODY = "#5C5C5C";
const LINE = "#E5E5E5";
const YEL = "#FFD60A";
const YEL_HI = "#FFF27A";
const YEL_LO = "#F2B800";
const YEL_EDGE = "#C99700";
const YEL_SOFT = "#FFF8CC";
const PINK = "#FF6BB5";
const YEL_GLOSS = `linear-gradient(180deg, ${YEL_HI} 0%, ${YEL} 52%, ${YEL_LO} 100%)`;

/* hover mix: yellow, raspberry, lime and red take turns across departments, tiles, tabs, buttons… */
const RASP = "#E30B5C";
const LIME = "#C8E17A";
const RASP_GLOSS = `linear-gradient(180deg, #FF5C95 0%, ${RASP} 52%, #B80848 100%)`;
const LIME_GLOSS = `linear-gradient(180deg, #E6F5B4 0%, ${LIME} 52%, #A9C95A 100%)`;
const RED_GLOSS = `linear-gradient(180deg, #FF5A5A 0%, ${RED} 52%, ${RED_DARK} 100%)`;
const HV = [
  { g: YEL_GLOSS, s: YEL, f: INK, e: YEL_EDGE },
  { g: RASP_GLOSS, s: RASP, f: "#fff", e: "#8F0636" },
  { g: LIME_GLOSS, s: LIME, f: INK, e: "#7E9A38" },
  { g: RED_GLOSS, s: RED, f: "#fff", e: "#800000" },
];
/** gives every 4 siblings matching `sel` a different hover colour set */
const cycle = (sel: string) =>
  HV.map((h, i) => `${sel}:nth-child(4n+${i + 1}){ --hg:${h.g}; --hs:${h.s}; --hf:${h.f}; --he:${h.e}; }`).join("\n");

const HOVER_MIX = `
/* ═════════ hover colour mix: yellow · raspberry · lime · red ═════════ */
.sh-page, .sh-portal{ --hg:${YEL_GLOSS}; --hs:${YEL}; --hf:${INK}; --he:${YEL_EDGE}; }
${cycle(".sh-strip > .sh-sc")}
${cycle(".sh-cside > div")}
${cycle(".sh-filter > div")}
${cycle(".sh-node-kids > div")}
${cycle(".sh-tiles > .sh-tile")}
${cycle(".sh-grid > .sh-card")}
${cycle(".sh-dock > .sh-tab")}
${cycle(".sh-menu > button")}
${cycle(".sh-fwrap > .sh-chip")}
${cycle(".sh-sizes > .sh-size")}
${cycle(".sh-ffoot > .sh-btn")}
${cycle(".sh-helpbtns > .sh-btn")}
${cycle(".sh-top-in > .sh-round")}
${cycle(".sh-mtop-in > .sh-mcirc")}
${cycle(".sh-mdock > .sh-mtab")}
${cycle(".sh-vrail > .sh-vact")}
${cycle(".sh-thumbs > .sh-thumb")}
${cycle(".sh-colors > .sh-dot")}
.sh-actions .sh-btn{ --hg:${RASP_GLOSS}; --hs:${RASP}; --hf:#fff; --he:#8F0636; }
.sh-buyrow .sh-btn{ --hg:${LIME_GLOSS}; --hs:${LIME}; --hf:${INK}; --he:#7E9A38; }
.sh-catbtn{ --hg:${LIME_GLOSS}; --hs:${LIME}; --hf:${INK}; --he:#7E9A38; }
.sh-cart{ --hg:${RASP_GLOSS}; --hs:${RASP}; --hf:#fff; }
.sh-eye{ --hg:${LIME_GLOSS}; --hs:${LIME}; --hf:${INK}; }
.sh-heart{ --hg:${RED_GLOSS}; --hs:${RED}; --hf:#fff; }

/* round + pill controls */
.sh-round:hover, .sh-catbtn:hover, .sh-chip:hover, .sh-tile:hover, .sh-size:hover:not(:disabled), .sh-nav:hover, .sh-fab:hover:not(:disabled),
.sh-round.on:hover, .sh-catbtn.on:hover, .sh-chip.on:hover, .sh-tile.on:hover, .sh-size.on:hover:not(:disabled){ background:var(--hg); color:var(--hf); }
.sh-heart:hover:not(:disabled) svg{ fill:var(--hf); stroke:var(--hf); }
.sh-x:hover{ background:var(--hg) !important; color:var(--hf); }
.sh-lb-btn:hover{ background:var(--hs); border-color:var(--hs); color:var(--hf) !important; }

/* department orbs + labels */
.sh-sc:hover .sh-orb{ --c:var(--hs); color:var(--hf); }
.sh-sc:hover .sh-sc-l, .sh-sc.on:hover .sh-sc-l{ background:var(--hs); color:var(--hf); }

/* category tree */
.sh-node:hover{ background:color-mix(in srgb, var(--hs) 24%, #fff); }
.sh-node:hover .sh-node-ic{ background:var(--hs); color:var(--hf); border-color:${INK}; }
.sh-node.on{ background:color-mix(in srgb, var(--hs) 24%, #fff); }

/* square cards */
.sh-card:hover{ box-shadow:0 0 0 5px var(--hs), 0 0 0 7px ${INK}, 0 26px 40px -16px rgba(0,0,0,.55); }
.sh-card:hover .sh-price{ background:var(--hg); color:var(--hf); }
.sh-step button:hover:not(:disabled), .sh-qty button:hover:not(:disabled){ background:var(--hs); color:var(--hf); }

/* buttons */
.sh-btn:hover:not(:disabled){ background:var(--hg); border-color:var(--he); color:var(--hf) !important; text-shadow:none;
  box-shadow:inset 0 2px 2px rgba(255,255,255,.7), inset 0 -4px 6px rgba(0,0,0,.18), 0 7px 0 var(--he), 0 16px 22px -6px color-mix(in srgb, var(--hs) 60%, transparent); }
.sh-btn.ghost:hover:not(:disabled){ background:var(--hg); border-color:${INK}; color:var(--hf) !important;
  box-shadow:inset 0 2px 2px rgba(255,255,255,.7), inset 0 -4px 6px rgba(0,0,0,.15), 0 7px 0 ${INK}, 0 16px 22px -6px color-mix(in srgb, var(--hs) 60%, transparent); }
.sh-btn:active:not(:disabled){ box-shadow:inset 0 2px 2px rgba(255,255,255,.6), inset 0 -3px 5px rgba(0,0,0,.2), 0 1px 0 var(--he), 0 4px 8px -4px rgba(0,0,0,.5); }

/* footer tabs + menu */
.sh-tab:hover, .sh-tab.on:hover{ background:var(--hg); color:var(--hf) !important; }
.sh-menu button:hover{ background:var(--hg); color:var(--hf); }
.sh-switch:hover{ background:color-mix(in srgb, var(--hs) 22%, #fff); }
.sh-thumb:hover{ border-color:var(--hs); }
.sh-dot:hover{ border-color:var(--hs); }

/* mobile feed */
.sh-mcirc:hover, .sh-mcirc:active{ background:var(--hs); border-color:var(--hs); color:var(--hf); }
.sh-vact .sh-vbtn:hover, .sh-vact .sh-vbtn:active{ background:var(--hg); color:var(--hf); }
.sh-mtab:hover:not(.center), .sh-mtab:active:not(.center){ color:var(--hs); }
.sh-mtab.center:hover .sh-browse, .sh-mtab.center:active .sh-browse{ background:var(--hg); color:var(--hf); }
.sh-mpill{ background:${LIME_GLOSS}; box-shadow:inset 0 1px 0 rgba(255,255,255,.8), 0 3px 0 #7E9A38; }
`;

const NineDots: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true" fill="currentColor">
    {[3, 9, 15].flatMap((y) => [3, 9, 15].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.7" />))}
  </svg>
);

/* ═══════════════════════════ STYLES ═══════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Jost:wght@300;400;500;600&display=swap');
.sh-page, .sh-page *, .sh-page *::before, .sh-page *::after,
.sh-portal, .sh-portal *, .sh-portal *::before, .sh-portal *::after{ box-sizing:border-box; font-family:'Jost',system-ui,sans-serif; }
.sh-page{ min-height:100vh; background:#fff; color:${INK}; overflow-x:hidden;
  padding:0 16px; padding-top:calc(96px + env(safe-area-inset-top)); padding-bottom:calc(130px + env(safe-area-inset-bottom)); }
/* :where keeps the reset at zero specificity so every button class below wins over it */
:where(.sh-page, .sh-portal) button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }
:where(.sh-page, .sh-portal) button:disabled{ cursor:not-allowed; opacity:.45; }
.sh-page button:focus-visible, .sh-page input:focus-visible, .sh-page [role=button]:focus-visible,
.sh-portal button:focus-visible, .sh-portal input:focus-visible{ outline:3px solid ${INK}; outline-offset:3px; }
.sh-wrap{ width:100%; max-width:1180px; margin:0 auto; }
.sh-wrap.wide{ max-width:none; }

/* ── fixed glass header (desktop) ── */
.sh-top{ position:fixed; top:0; left:0; right:0; z-index:200; padding-top:env(safe-area-inset-top);
  background:rgba(255,255,255,.62); -webkit-backdrop-filter:blur(22px) saturate(180%); backdrop-filter:blur(22px) saturate(180%);
  border-bottom:1px solid rgba(0,0,0,.07); box-shadow:0 10px 30px -20px rgba(0,0,0,.35); }
.sh-top-in{ display:flex; align-items:center; gap:12px; max-width:1180px; margin:0 auto; padding:14px 20px; }
.sh-round{ position:relative; width:46px; height:46px; border-radius:50%; border:1px solid ${INK}; display:flex; align-items:center; justify-content:center;
  background:linear-gradient(180deg,#fff 0%,#f0f0f0 100%); box-shadow:inset 0 2px 1px #fff, inset 0 -3px 5px rgba(0,0,0,.1), 0 0 0 4px ${INK}; transition:all .25s; }
.sh-round:hover{ background:${YEL_GLOSS}; color:${INK}; transform:translateY(-2px); box-shadow:inset 0 2px 1px rgba(255,255,255,.9), inset 0 -3px 5px rgba(0,0,0,.12), 0 0 0 4px ${INK}; }
.sh-round.on{ background:${INK}; color:#fff; }
.sh-round.on:hover{ background:${YEL_GLOSS}; color:${INK}; }
.sh-badge{ position:absolute; top:-7px; right:-7px; min-width:20px; height:20px; padding:0 5px; border-radius:999px; background:${RED}; color:#fff;
  font-style:normal; font-size:10.5px; font-weight:600; display:flex; align-items:center; justify-content:center; border:2px solid #fff; }

.sh-menu{ position:absolute; right:0; top:calc(100% + 14px); width:230px; background:#fff; border:1px solid ${INK}; border-radius:20px; overflow:hidden; z-index:300; color:${INK};
  box-shadow:0 0 0 3px #fff, 0 0 0 4px ${INK}, 0 24px 50px -20px rgba(0,0,0,.45); }
.sh-menu button{ display:flex; align-items:center; gap:12px; width:100%; padding:14px 18px; text-align:left; font-size:11px; font-weight:500; letter-spacing:.18em; text-transform:uppercase;
  border-bottom:1px solid ${LINE}; transition:background .2s; }
.sh-menu button:last-child{ border-bottom:none; }
.sh-menu button:hover{ background:${YEL_GLOSS}; color:${INK}; }

.sh-catbtn{ flex:none; display:inline-flex; align-items:center; justify-content:center; gap:10px; height:48px; padding:0 22px; border-radius:999px; border:1px solid ${INK};
  background:linear-gradient(180deg,#fff 0%,#f0f0f0 100%); font-size:11.5px; font-weight:500; letter-spacing:.2em; text-transform:uppercase;
  box-shadow:inset 0 2px 1px #fff, inset 0 -3px 5px rgba(0,0,0,.1), 0 0 0 4px ${INK}; transition:all .25s; }
.sh-catbtn:hover{ background:${YEL_GLOSS}; color:${INK}; transform:translateY(-2px); }
.sh-catbtn.on{ background:${INK}; color:#fff; }
.sh-catbtn.on:hover{ background:${YEL_GLOSS}; color:${INK}; }
.sh-search{ flex:1; min-width:0; display:flex; align-items:center; gap:10px; height:48px; padding:0 18px; border:1px solid ${INK}; border-radius:999px; background:rgba(255,255,255,.85);
  box-shadow:inset 0 2px 4px rgba(0,0,0,.06), 0 0 0 4px ${INK}; transition:all .25s; }
.sh-search:focus-within, .sh-search:hover{ background:${YEL_SOFT}; box-shadow:inset 0 2px 4px rgba(0,0,0,.06), 0 0 0 4px ${YEL}, 0 0 0 5px ${INK}; }
.sh-search input{ flex:1; min-width:0; border:none; outline:none; background:transparent; font-size:15px; height:100%; color:${INK}; }
.sh-search input::placeholder{ color:#A3A3A3; }
.sh-search button{ display:flex; color:${BODY}; }
.sh-search button:hover{ color:${INK}; }
.sh-filterbtn{ position:relative; flex:none; display:inline-flex; align-items:center; gap:8px; height:48px; padding:0 22px; border-radius:999px; background:${INK}; color:#fff !important;
  font-size:11.5px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; transition:background .25s, transform .25s; }
.sh-filterbtn:hover{ background:${YEL_GLOSS}; color:${INK} !important; transform:translateY(-1px); }

.sh-shell{ display:grid; grid-template-columns:minmax(0,1fr); gap:26px; align-items:start; }
.sh-shell.side{ grid-template-columns:270px minmax(0,1fr); }
.sh-main{ min-width:0; }
.sh-cside{ position:sticky; top:calc(100px + env(safe-area-inset-top)); max-height:calc(100vh - 124px); overflow-y:auto; scrollbar-width:none; border:1px solid ${INK}; border-radius:24px; padding:20px 16px; background:linear-gradient(180deg,#fff,#fafafa);
  box-shadow:inset 0 2px 0 #fff, 0 14px 24px -16px rgba(0,0,0,.35); }
.sh-cside::-webkit-scrollbar{ display:none; }
.sh-cside h4{ margin:0 0 12px 6px; font-family:'Cormorant Garamond',serif; font-size:28px; font-weight:600; }

.sh-chips{ display:flex; gap:8px; overflow-x:auto; padding:6px 4px; margin:0 -4px; scrollbar-width:none; -webkit-overflow-scrolling:touch; }
.sh-chips::-webkit-scrollbar{ display:none; }
.sh-chip{ flex-shrink:0; display:inline-flex; align-items:center; gap:7px; height:38px; padding:0 18px; border-radius:999px; border:1px solid ${INK};
  background:linear-gradient(180deg,#fff 0%,#f1f1f1 100%); box-shadow:inset 0 2px 1px #fff, 0 3px 0 ${INK};
  font-size:11.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; white-space:nowrap; transition:all .2s; }
.sh-chip:hover{ background:${YEL_GLOSS}; color:${INK}; transform:translateY(-1px); }
.sh-chip.on{ background:${INK}; color:#fff; border-color:${INK}; }
.sh-chip.on:hover{ background:${YEL_GLOSS}; color:${INK}; }

/* category strip: every category, glossy orbs */
.sh-strip{ display:flex; gap:18px; overflow-x:auto; padding:14px 8px 16px; margin:0 -4px; scrollbar-width:none; -webkit-overflow-scrolling:touch; }
.sh-strip::-webkit-scrollbar{ display:none; }
.sh-sc{ flex:none; width:84px; display:flex; flex-direction:column; align-items:center; gap:9px; }
.sh-sc-l{ font-size:10px; font-weight:500; letter-spacing:.12em; text-transform:uppercase; text-align:center; line-height:1.3; color:${INK}; padding:3px 9px; border-radius:999px; transition:background .2s; }
.sh-sc:hover .sh-sc-l{ background:${YEL}; }
.sh-orb{ --c:#0a0a0a; position:relative; isolation:isolate; width:68px; height:68px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; font-size:26px;
  background:radial-gradient(circle at 35% 28%, color-mix(in srgb, var(--c) 60%, #fff) 0, var(--c) 45%, color-mix(in srgb, var(--c) 55%, #000) 100%);
  box-shadow:inset -6px -8px 14px rgba(0,0,0,.45), inset 3px 3px 8px rgba(255,255,255,.35), 0 10px 14px -6px rgba(0,0,0,.45);
  transition:transform .3s cubic-bezier(.2,.9,.3,1.4), box-shadow .25s, background .25s, color .25s; }
.sh-sc:hover .sh-orb{ --c:${YEL}; color:${INK}; transform:translateY(-4px) scale(1.07); box-shadow:inset -6px -8px 14px rgba(120,80,0,.35), inset 3px 3px 8px rgba(255,255,255,.7), 0 0 0 3px #fff, 0 0 0 5px ${INK}, 0 14px 18px -6px rgba(0,0,0,.45); }
.sh-sc:hover .sh-orb img, .sh-sc:hover .sh-orb svg{ filter:drop-shadow(0 2px 2px rgba(0,0,0,.25)); }
.sh-sc.on .sh-orb{ --c:#fff; color:${INK}; transform:translateY(-3px) scale(1.08);
  box-shadow:inset -6px -8px 14px rgba(0,0,0,.28), inset 3px 3px 8px rgba(255,255,255,.7), 0 0 0 3px #fff, 0 0 0 5px ${INK}, 0 14px 18px -6px rgba(0,0,0,.5); }
.sh-sc.on .sh-sc-l{ background:${INK}; color:#fff; }
.sh-sc.on:hover .sh-sc-l{ background:${YEL}; color:${INK}; }
.sh-orb::before{ content:""; position:absolute; z-index:2; top:6%; left:14%; width:52%; height:34%; border-radius:50%;
  background:linear-gradient(180deg, rgba(255,255,255,.9), rgba(255,255,255,.05)); pointer-events:none; }
.sh-orb::after{ content:""; position:absolute; z-index:2; right:15%; bottom:11%; width:26%; height:13%; border-radius:50%; background:rgba(255,255,255,.4);
  transform:rotate(-38deg); filter:blur(.6px); pointer-events:none; }
.sh-orb img{ position:relative; z-index:1; width:60%; height:60%; object-fit:contain; display:block; filter:drop-shadow(0 3px 3px rgba(0,0,0,.35)); }
.sh-orb svg{ position:relative; z-index:1; filter:drop-shadow(0 2px 2px rgba(0,0,0,.35)); }
.sh-sc:active .sh-orb{ transform:scale(.94); }

/* shop by category tiles */
.sh-tiles{ display:grid; grid-template-columns:repeat(auto-fill,minmax(160px,1fr)); gap:10px; margin:0 4px 14px; }
.sh-tile{ display:flex; align-items:center; gap:12px; padding:14px; border:1px solid ${INK}; border-radius:18px; background:linear-gradient(180deg,#fff,#f4f4f4); box-shadow:inset 0 2px 1px #fff, 0 3px 0 ${INK}; text-align:left; transition:all .2s; }
.sh-tile:hover{ background:${YEL_GLOSS}; color:${INK}; transform:translateY(-2px); }
.sh-tile.on{ background:${INK}; border-color:${INK}; color:#fff; }
.sh-tile.on:hover{ background:${YEL_GLOSS}; color:${INK}; }
.sh-tile-ic{ display:flex; font-size:20px; flex:none; }
.sh-tile-n{ flex:1; min-width:0; font-size:13px; font-weight:500; letter-spacing:.04em; line-height:1.25; }

/* category tree */
.sh-node{ display:flex; align-items:center; gap:12px; width:100%; padding:9px 8px; border-radius:14px; text-align:left; font-size:13px; font-weight:400; letter-spacing:.04em; transition:background .2s; }
.sh-node:hover{ background:${YEL_SOFT}; }
.sh-node:hover .sh-node-ic{ background:${YEL}; border-color:${INK}; color:${INK}; }
.sh-node.trail{ font-weight:500; }
.sh-node.on{ font-weight:600; background:${YEL_SOFT}; }
.sh-node-ic{ width:32px; height:32px; border-radius:50%; border:1px solid ${INK}; display:flex; align-items:center; justify-content:center; font-size:14px; flex:none;
  background:linear-gradient(180deg,#fff,#eee); color:${INK}; box-shadow:inset 0 2px 1px #fff, 0 2px 0 rgba(0,0,0,.25); transition:all .2s; }
.sh-node.on .sh-node-ic{ background:${INK}; color:#fff; }
.sh-node-name{ flex:1; min-width:0; }
.sh-node-ct{ display:inline-flex; align-items:center; justify-content:center; min-width:24px; height:22px; padding:0 7px; border-radius:999px; background:#F1F1F1; font-size:11px; font-weight:600; color:${BODY}; }
.sh-node-ct.zero{ opacity:.45; }
.sh-node:hover .sh-node-ct{ background:#fff; }
.sh-node-chev{ display:flex; color:${BODY}; }
.sh-node-kids{ padding-left:14px; border-left:2px solid ${LINE}; margin:2px 0 6px 18px; }
.sh-node-all{ margin-bottom:6px; border-bottom:1px solid ${LINE}; border-radius:14px; padding-bottom:12px; }

/* ── square glossy cards (desktop) ── */
.sh-grid{ display:grid; grid-template-columns:repeat(auto-fill,minmax(250px,1fr)); gap:24px; margin-top:20px; padding:6px; }
.sh-card{ position:relative; isolation:isolate; aspect-ratio:1/1; border-radius:30px; overflow:hidden; cursor:pointer; border:2px solid ${INK}; background:#F4F1EA;
  box-shadow:inset 0 3px 0 rgba(255,255,255,.9), 0 14px 22px -12px rgba(0,0,0,.5);
  transition:transform .3s cubic-bezier(.2,.9,.3,1.2), box-shadow .3s, border-color .25s; }
.sh-card::before{ content:""; position:absolute; z-index:1; top:6px; left:10%; right:10%; height:22px; border-radius:999px; pointer-events:none;
  background:linear-gradient(180deg,rgba(255,255,255,.75),rgba(255,255,255,0)); }
.sh-card::after{ content:""; position:absolute; z-index:1; inset:0; pointer-events:none; transform:translateX(-130%); transition:transform .7s ease;
  background:linear-gradient(115deg, rgba(255,255,255,0) 35%, rgba(255,255,255,.55) 48%, rgba(255,255,255,0) 61%); }
.sh-card:hover{ transform:translateY(-7px) scale(1.012); border-color:${INK}; box-shadow:0 0 0 5px ${YEL}, 0 0 0 7px ${INK}, 0 26px 40px -16px rgba(0,0,0,.55); }
.sh-card:hover::after{ transform:translateX(130%); }
.sh-media{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:#6B6B6B; }
.sh-media img{ width:100%; height:100%; object-fit:cover; display:block; transition:transform .5s cubic-bezier(.22,.8,.26,1); user-select:none; -webkit-user-drag:none; }
.sh-card:hover .sh-media img{ transform:scale(1.06); }
.sh-media::after{ content:""; position:absolute; z-index:1; left:0; right:0; bottom:0; height:46%; pointer-events:none; background:linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,.62)); }
.sh-fab{ position:absolute; z-index:3; width:44px; height:44px; border-radius:50%; background:linear-gradient(180deg,#fff,#ececec); color:${INK}; display:flex; align-items:center; justify-content:center;
  box-shadow:inset 0 2px 1px #fff, 0 3px 0 rgba(0,0,0,.35), 0 8px 12px -6px rgba(0,0,0,.5); transition:all .2s; }
.sh-fab:hover:not(:disabled){ transform:scale(1.12); background:${YEL_GLOSS}; color:${INK}; }
.sh-heart{ top:12px; left:12px; }
.sh-heart.on{ color:${RED}; }
.sh-heart:hover:not(:disabled) svg{ fill:${RED}; stroke:${RED}; }
.sh-eye{ top:64px; left:12px; }
.sh-cart{ bottom:12px; right:12px; }
.sh-cart .sh-plus{ position:absolute; left:50%; top:50%; width:12px; height:12px; transform:translate(-50%,-30%); opacity:0; transition:opacity .2s; }
.sh-cart:hover:not(:disabled) .sh-plus{ opacity:1; }
.sh-fab svg{ width:20px; height:20px; }
.sh-tag{ position:absolute; top:14px; right:14px; z-index:3; background:${RED}; color:#fff; font-size:9.5px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; padding:5px 11px; border-radius:999px;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.45), 0 3px 0 ${RED_DARK}; }
.sh-step{ position:absolute; bottom:12px; right:12px; z-index:3; display:inline-flex; align-items:center; gap:2px; padding:3px; border-radius:999px; background:#fff; color:${INK}; box-shadow:0 3px 0 rgba(0,0,0,.35); }
.sh-step button{ width:30px; height:30px; border-radius:50%; display:flex; align-items:center; justify-content:center; background:${INK}; color:#fff; transition:background .2s, color .2s; }
.sh-step button:hover:not(:disabled){ background:${YEL}; color:${INK}; }
.sh-step b{ min-width:22px; text-align:center; font-size:13px; font-weight:600; }
.sh-info{ position:absolute; z-index:3; left:14px; right:72px; bottom:14px; display:flex; flex-direction:column; align-items:flex-start; gap:7px; min-width:0; pointer-events:none; }
.sh-name{ max-width:100%; font-family:'Cormorant Garamond',serif; font-size:25px; font-weight:700; line-height:1.1; color:#fff; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; text-shadow:0 2px 8px rgba(0,0,0,.55); }
.sh-price{ display:inline-flex; align-items:center; padding:4px 13px; border-radius:999px; background:linear-gradient(180deg,#fff,#ececec); color:${INK}; font-size:16px; font-weight:600;
  box-shadow:inset 0 1px 0 #fff, 0 3px 0 rgba(0,0,0,.4); transition:background .25s; }
.sh-card:hover .sh-price{ background:${YEL_GLOSS}; }

.sh-empty{ display:flex; flex-direction:column; align-items:center; gap:10px; text-align:center; padding:60px 20px; }
.sh-empty h3{ font-family:'Cormorant Garamond',serif; font-size:30px; font-weight:600; margin:6px 0 0; }
.sh-empty p{ margin:0; color:${BODY}; }

.sh-btn{ position:relative; isolation:isolate; overflow:hidden; display:inline-flex; align-items:center; justify-content:center; gap:8px; height:50px; padding:0 28px; border-radius:999px;
  background:linear-gradient(180deg,#4a4a4a 0%,#111 48%,#000 100%); border:1px solid #000;
  color:#fff !important; font-size:11.5px; font-weight:600; letter-spacing:.22em; text-transform:uppercase; text-shadow:0 1px 2px rgba(0,0,0,.5);
  box-shadow:inset 0 2px 2px rgba(255,255,255,.4), inset 0 -4px 6px rgba(0,0,0,.55), 0 5px 0 #2b2b2b, 0 12px 18px -6px rgba(0,0,0,.55);
  transition:background .25s, border-color .25s, color .25s, transform .15s, box-shadow .15s; }
.sh-btn::before{ content:""; position:absolute; z-index:-1; left:7%; right:7%; top:3px; height:46%; border-radius:999px;
  background:linear-gradient(180deg,rgba(255,255,255,.55),rgba(255,255,255,.05)); pointer-events:none; }
.sh-btn:hover:not(:disabled){ background:${YEL_GLOSS}; border-color:${YEL_EDGE}; color:${INK} !important; text-shadow:0 1px 0 rgba(255,255,255,.6); transform:translateY(-2px);
  box-shadow:inset 0 2px 2px rgba(255,255,255,.8), inset 0 -4px 6px rgba(160,100,0,.25), 0 7px 0 ${YEL_EDGE}, 0 16px 22px -6px rgba(242,184,0,.55); }
.sh-btn:active:not(:disabled){ transform:translateY(4px);
  box-shadow:inset 0 2px 2px rgba(255,255,255,.6), inset 0 -3px 5px rgba(160,100,0,.25), 0 1px 0 ${YEL_EDGE}, 0 4px 8px -4px rgba(0,0,0,.5); }
.sh-btn:disabled{ box-shadow:inset 0 2px 2px rgba(255,255,255,.25), inset 0 -3px 5px rgba(0,0,0,.4), 0 3px 0 #2b2b2b; }
.sh-btn.ghost{ background:linear-gradient(180deg,#fff 0%,#f1f1f1 55%,#dcdcdc 100%); color:${INK} !important; text-shadow:0 1px 0 rgba(255,255,255,.8);
  box-shadow:inset 0 2px 2px #fff, inset 0 -4px 6px rgba(0,0,0,.12), 0 5px 0 #000, 0 12px 18px -6px rgba(0,0,0,.45); }
.sh-btn.ghost::before{ background:linear-gradient(180deg,rgba(255,255,255,.95),rgba(255,255,255,.1)); }
.sh-btn.ghost:hover:not(:disabled){ background:${YEL_GLOSS}; border-color:${INK}; color:${INK} !important;
  box-shadow:inset 0 2px 2px rgba(255,255,255,.8), inset 0 -4px 6px rgba(160,100,0,.2), 0 7px 0 ${INK}, 0 16px 22px -6px rgba(242,184,0,.5); }
.sh-btn.ghost:active:not(:disabled){ transform:translateY(4px); box-shadow:inset 0 2px 2px #fff, inset 0 -3px 5px rgba(0,0,0,.12), 0 1px 0 ${INK}, 0 4px 8px -4px rgba(0,0,0,.4); }
.sh-btn.block{ width:100%; }

/* ── floating footer bar (desktop) ── */
.sh-dock{ position:fixed; left:50%; bottom:calc(22px + env(safe-area-inset-bottom)); transform:translateX(-50%); z-index:150; display:flex; align-items:center; gap:4px; padding:7px;
  border-radius:999px; background:linear-gradient(180deg,#2a2a2a,#000 60%); color:#fff; box-shadow:inset 0 2px 1px rgba(255,255,255,.25), 0 0 0 3px ${YEL}, 0 0 0 4px ${INK}, 0 20px 44px -14px rgba(0,0,0,.55); animation:shRise .35s ease-out; }
.sh-tab{ position:relative; display:inline-flex; align-items:center; gap:10px; height:46px; padding:0 22px; border-radius:999px; color:#fff !important;
  font-size:11px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; white-space:nowrap; -webkit-tap-highlight-color:transparent; transition:background .2s, color .2s, transform .2s; }
.sh-tab .ic{ position:relative; display:flex; }
.sh-tab .sh-badge{ top:-9px; right:-11px; }
.sh-tab:hover{ background:${YEL_GLOSS}; color:${INK} !important; transform:translateY(-1px); }
.sh-tab.on{ background:linear-gradient(180deg,#fff,#e8e8e8); color:${INK} !important; }
.sh-tab.on:hover{ background:${YEL_GLOSS}; color:${INK} !important; }
@keyframes shRise{ from{ opacity:0; transform:translate(-50%, 14px); } to{ opacity:1; transform:translate(-50%, 0); } }

.sh-toast{ position:fixed; left:50%; bottom:calc(104px + env(safe-area-inset-bottom)); transform:translateX(-50%); z-index:500; max-width:calc(100% - 32px);
  background:linear-gradient(180deg,#fff,#f1f1f1); color:${INK}; border:1px solid ${INK}; font-size:11.5px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; padding:12px 22px; border-radius:999px;
  box-shadow:0 0 0 3px ${YEL}, 0 0 0 4px ${INK}; animation:shToast .25s ease-out; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
@keyframes shToast{ from{ opacity:0; transform:translate(-50%, 10px); } to{ opacity:1; transform:translate(-50%, 0); } }

/* ═══════════════ MOBILE: full-screen vertical feed, JOJO style ═══════════════ */
.sh-page.m{ padding:0; min-height:100dvh; background:#0b0b0e; overflow:hidden; }
.sh-page.m .sh-toast{ bottom:calc(120px + env(safe-area-inset-bottom)); background:rgba(20,20,20,.88); color:#fff; border-color:rgba(255,255,255,.3); box-shadow:none; }
.sh-feed{ position:fixed; inset:0; overflow-y:scroll; scroll-snap-type:y mandatory; overscroll-behavior:contain; -webkit-overflow-scrolling:touch; scrollbar-width:none; background:#0b0b0e; }
.sh-feed::-webkit-scrollbar{ display:none; }
.sh-slide{ position:relative; height:100vh; height:100dvh; scroll-snap-align:start; scroll-snap-stop:always; overflow:hidden; color:#fff; cursor:pointer; }
.sh-slide-img{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; user-select:none; -webkit-user-drag:none; }
.sh-slide-ph{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:rgba(255,255,255,.35);
  background:radial-gradient(circle at 50% 38%, #3a3d46 0%, #17181d 70%, #0b0b0e 100%); }
.sh-slide-shade{ position:absolute; inset:0; pointer-events:none;
  background:linear-gradient(180deg, rgba(0,0,0,.38) 0%, rgba(0,0,0,0) 22%, rgba(0,0,0,0) 50%, rgba(0,0,0,.78) 100%); }

.sh-mtop{ position:fixed; top:0; left:0; right:0; z-index:200; padding-top:env(safe-area-inset-top); color:#fff;
  background:linear-gradient(180deg, rgba(0,0,0,.42), rgba(0,0,0,.14)); -webkit-backdrop-filter:blur(14px); backdrop-filter:blur(14px); border-bottom:1px solid rgba(255,255,255,.14); }
.sh-mtop-in{ display:flex; align-items:center; gap:9px; padding:12px 14px; }
.sh-logo{ display:block; height:44px; width:auto; max-width:96px; object-fit:contain; flex:none; filter:drop-shadow(0 1px 3px rgba(0,0,0,.35)); }
.sh-cur{ flex:1; min-width:0; display:inline-flex; align-items:center; gap:8px; margin-left:6px; font-size:17px; font-weight:400; color:#fff; }
.sh-mcirc{ position:relative; flex:none; width:42px; height:42px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff;
  background:rgba(255,255,255,.2); border:1px solid rgba(255,255,255,.38); transition:background .2s, color .2s, transform .2s; }
.sh-mcirc:hover, .sh-mcirc:active{ background:${YEL}; border-color:${YEL}; color:${INK}; }
.sh-mcirc .sh-badge{ top:-5px; right:-5px; }
.sh-mtop .sh-menu{ right:14px; top:calc(100% - 2px); }
.sh-mbar{ position:fixed; z-index:190; left:14px; right:14px; top:calc(env(safe-area-inset-top) + 78px); display:flex; flex-direction:column; gap:8px; pointer-events:none; }
.sh-msearch{ pointer-events:auto; display:flex; align-items:center; gap:10px; height:46px; padding:0 16px; border-radius:999px; color:#fff;
  background:rgba(20,20,20,.55); -webkit-backdrop-filter:blur(16px); backdrop-filter:blur(16px); border:1px solid rgba(255,255,255,.35); }
.sh-msearch:focus-within{ border-color:${YEL}; }
.sh-msearch input{ flex:1; min-width:0; border:none; outline:none; background:transparent; color:#fff; font-size:15px; height:100%; }
.sh-msearch input::placeholder{ color:rgba(255,255,255,.65); }
.sh-msearch button{ display:flex; color:#fff; }
.sh-mpill{ pointer-events:auto; align-self:flex-start; display:inline-flex; align-items:center; gap:8px; max-width:100%; height:34px; padding:0 8px 0 14px; border-radius:999px; font-size:12px; font-weight:500; letter-spacing:.06em;
  background:${YEL_GLOSS}; color:${INK}; box-shadow:inset 0 1px 0 rgba(255,255,255,.8), 0 3px 0 ${YEL_EDGE}; }
.sh-mpill span{ overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.sh-mpill button{ width:22px; height:22px; border-radius:50%; background:${INK}; color:#fff; display:flex; align-items:center; justify-content:center; flex:none; }

.sh-vrail{ position:absolute; z-index:3; right:12px; bottom:calc(122px + env(safe-area-inset-bottom)); display:flex; flex-direction:column; align-items:center; gap:14px; }
.sh-vact{ display:flex; flex-direction:column; align-items:center; gap:6px; font-size:13px; font-weight:400; text-shadow:0 1px 4px rgba(0,0,0,.5); }
.sh-vbtn{ width:56px; height:56px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff;
  background:rgba(255,255,255,.3); -webkit-backdrop-filter:blur(14px) saturate(150%); backdrop-filter:blur(14px) saturate(150%);
  border:1px solid rgba(255,255,255,.3); box-shadow:inset 0 1px 0 rgba(255,255,255,.45), 0 6px 14px -6px rgba(0,0,0,.5); transition:background .2s, color .2s, transform .2s; }
.sh-vbtn.dark{ background:rgba(0,0,0,.4); }
.sh-vbtn.on{ color:${RED}; }
.sh-vbtn.buy{ width:60px; height:60px; border:none; background:linear-gradient(180deg,#ff9ccf 0%,${PINK} 55%,#f0509f 100%); color:${INK};
  box-shadow:inset 0 2px 2px rgba(255,255,255,.7), inset 0 -4px 6px rgba(160,20,90,.3), 0 8px 16px -6px rgba(240,80,160,.7); }
.sh-vbtn:hover, .sh-vbtn:active{ background:${YEL_GLOSS}; color:${INK}; transform:scale(.95); }
.sh-vbtn:hover svg, .sh-vbtn:active svg{ fill:none; }
.sh-vbtn.on:hover svg{ fill:${RED}; }

.sh-vinfo{ position:absolute; z-index:3; left:18px; right:92px; bottom:calc(98px + env(safe-area-inset-bottom)); pointer-events:none; text-shadow:0 1px 8px rgba(0,0,0,.45); }
.sh-vcat{ display:flex; align-items:center; gap:10px; font-size:14px; font-weight:500; letter-spacing:.22em; text-transform:uppercase; color:rgba(255,255,255,.82); }
.sh-vtag{ font-size:10px; font-weight:600; letter-spacing:.12em; padding:3px 9px; border-radius:999px; background:${RED}; color:#fff; text-shadow:none; }
.sh-vname{ margin:6px 0 0; font-size:28px; font-weight:500; line-height:1.15; color:#fff; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.sh-vdesc{ margin:10px 0 0; font-size:15px; line-height:1.35; color:rgba(255,255,255,.75); display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.sh-vprice{ margin-top:14px; font-size:27px; font-weight:500; color:#fff; }

.sh-mdock{ position:fixed; left:0; right:0; bottom:0; z-index:150; display:flex; align-items:flex-end; justify-content:space-between; padding:0 6px calc(10px + env(safe-area-inset-bottom)); min-height:calc(86px + env(safe-area-inset-bottom));
  border-radius:28px 28px 0 0; color:#fff; background:linear-gradient(90deg, rgba(120,70,45,.5), rgba(35,30,28,.5) 50%, rgba(95,100,50,.5));
  -webkit-backdrop-filter:blur(22px) saturate(160%); backdrop-filter:blur(22px) saturate(160%); border-top:1px solid rgba(255,255,255,.2); }
.sh-mtab{ position:relative; flex:1; min-width:0; display:flex; flex-direction:column; align-items:center; justify-content:flex-end; gap:6px; padding:22px 0 0; font-size:13px; font-weight:400; color:#fff; -webkit-tap-highlight-color:transparent; transition:color .2s; }
.sh-mtab .ic{ display:flex; }
.sh-mtab span:last-child{ white-space:nowrap; }
.sh-mtab.on{ color:${PINK}; }
.sh-mtab.on::before{ content:""; position:absolute; top:6px; left:50%; width:40px; height:3px; margin-left:-20px; border-radius:999px; background:${PINK}; }
.sh-mtab:hover:not(.center), .sh-mtab:active:not(.center){ color:${YEL}; }
.sh-mtab.center{ padding-top:0; }
.sh-browse{ display:flex; align-items:center; justify-content:center; width:64px; height:64px; margin-top:-34px; border-radius:50%; color:${INK};
  background:linear-gradient(180deg,#fff,#f3efe9); border:1px solid rgba(255,255,255,.7); box-shadow:inset 0 2px 1px #fff, inset 0 -3px 5px rgba(0,0,0,.1), 0 8px 18px -6px rgba(0,0,0,.55); transition:background .2s, transform .2s; }
.sh-mtab.center:hover .sh-browse, .sh-mtab.center:active .sh-browse{ background:${YEL_GLOSS}; transform:scale(.95); }
.sh-vempty{ height:100vh; height:100dvh; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; text-align:center; padding:0 28px; color:#fff; }
.sh-vempty h3{ margin:6px 0 0; font-size:28px; font-weight:500; }
.sh-vempty p{ margin:0; color:rgba(255,255,255,.7); }

/* ── overlays ── */
.sh-scrim{ position:fixed; inset:0; z-index:400; display:flex; background:rgba(0,0,0,.5); animation:shFade .2s ease-out; }
.sh-scrim.desktop{ align-items:center; justify-content:center; padding:24px; }
.sh-scrim.mobile{ align-items:flex-end; justify-content:center; }
.sh-panel{ position:relative; display:flex; flex-direction:column; width:100%; background:#fff; color:${INK}; border:1px solid ${INK}; }
.sh-panel::after{ content:""; position:absolute; inset:6px; border:1px solid ${INK}; pointer-events:none; z-index:5; }
.sh-panel.desktop{ max-width:480px; max-height:88vh; border-radius:30px; box-shadow:0 30px 80px -20px rgba(0,0,0,.5); animation:shPop .25s cubic-bezier(.2,.9,.3,1.1); }
.sh-panel.desktop::after{ border-radius:24px; }
.sh-panel.desktop.wide{ max-width:900px; }
.sh-panel.mobile{ max-height:92vh; border-radius:28px 28px 0 0; padding-bottom:env(safe-area-inset-bottom); box-shadow:0 -20px 60px -20px rgba(0,0,0,.5); animation:shSlide .3s cubic-bezier(.2,.9,.3,1); }
.sh-panel.mobile::after{ border-radius:22px 22px 0 0; border-bottom:none; }
.sh-handle{ width:42px; height:4px; border-radius:999px; background:#CFCFCF; margin:14px auto 0; flex-shrink:0; position:relative; z-index:6; }
.sh-scroll{ flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; }
.sh-x{ position:absolute; top:16px; right:16px; z-index:7; width:38px; height:38px; border-radius:50%; background:linear-gradient(180deg,#fff,#eee) !important; border:1px solid ${INK} !important; color:${INK};
  display:flex; align-items:center; justify-content:center; transition:all .2s; }
.sh-x:hover{ background:${YEL_GLOSS} !important; color:${INK}; }
@keyframes shFade{ from{ opacity:0; } to{ opacity:1; } }
@keyframes shPop{ from{ opacity:0; transform:scale(.95) translateY(12px); } to{ opacity:1; transform:scale(1) translateY(0); } }
@keyframes shSlide{ from{ transform:translateY(100%); } to{ transform:translateY(0); } }

.sh-detail{ display:grid; grid-template-columns:1fr; gap:20px; padding:20px 20px 26px; }
.sh-panel.desktop.wide .sh-detail{ grid-template-columns:1fr 1fr; gap:30px; padding:24px 24px 28px; align-items:start; }
.sh-gal{ position:relative; }
.sh-gal-main{ position:relative; background:#F4F1EA; border:1px solid ${INK}; box-shadow:0 0 0 3px #fff, 0 0 0 4px ${INK}; border-radius:18px; aspect-ratio:3/4; overflow:hidden; display:flex; align-items:center; justify-content:center; color:#6B6B6B; }
.sh-gal-main.zoom{ cursor:zoom-in; }
.sh-panel.mobile .sh-gal-main{ aspect-ratio:4/5; margin-top:6px; }
.sh-gal-main img{ width:100%; height:100%; object-fit:contain; display:block; }
.sh-nav{ position:absolute; top:50%; transform:translateY(-50%); width:38px; height:38px; border-radius:50%; background:linear-gradient(180deg,#fff,#e9e9e9); color:${INK}; display:flex; align-items:center; justify-content:center; box-shadow:0 3px 0 rgba(0,0,0,.3); transition:all .2s; }
.sh-nav:hover{ background:${YEL_GLOSS}; color:${INK}; }
.sh-nav.l{ left:10px; } .sh-nav.r{ right:10px; }
.sh-thumbs{ display:flex; gap:8px; margin-top:10px; overflow-x:auto; scrollbar-width:none; }
.sh-thumbs::-webkit-scrollbar{ display:none; }
.sh-thumb{ flex-shrink:0; width:54px; height:68px; border-radius:10px; overflow:hidden; background:#F4F1EA; border:2px solid transparent; padding:0; transition:border-color .2s; }
.sh-thumb img{ width:100%; height:100%; object-fit:contain; display:block; }
.sh-thumb:hover{ border-color:${YEL}; }
.sh-thumb.on{ border-color:${INK}; }
.sh-side{ display:flex; flex-direction:column; min-width:0; }
.sh-crumb{ font-size:10.5px; font-weight:500; letter-spacing:.22em; text-transform:uppercase; color:${BODY}; padding-right:48px; }
.sh-pname{ font-family:'Cormorant Garamond',serif; font-weight:700; font-size:clamp(32px,5vw,44px); line-height:1.05; margin:10px 0 6px; }
.sh-pprice{ font-size:22px; font-weight:500; }
.sh-meta{ display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:14px; }
.sh-meta div{ border:1px solid ${LINE}; border-radius:12px; padding:9px 12px; }
.sh-meta small{ display:block; font-size:9.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; color:${BODY}; margin-bottom:2px; }
.sh-meta span{ font-size:13px; }
.sh-stock{ display:inline-flex; align-self:flex-start; margin-top:16px; font-size:10px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; padding:5px 12px; border-radius:999px; border:1px solid #CFCFCF; color:${BODY}; }
.sh-stock.low{ border-color:${RED}; color:${RED}; }
.sh-note{ margin:12px 0 0; font-size:12px; font-weight:500; letter-spacing:.1em; text-transform:uppercase; color:${RED}; }
.sh-opt{ margin-top:16px; }
.sh-opt small{ display:block; font-size:10px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; color:${BODY}; margin-bottom:8px; }
.sh-opt small b{ color:${INK}; font-weight:600; }
.sh-colors{ display:flex; flex-wrap:wrap; gap:10px; }
.sh-dot{ position:relative; width:42px; height:42px; border-radius:50%; padding:4px; background:none; border:2px solid transparent; transition:border-color .2s, transform .25s cubic-bezier(.2,.9,.3,1.4); }
.sh-dot i{ position:relative; display:block; width:100%; height:100%; border-radius:50%;
  background:
    radial-gradient(circle at 30% 24%, #fff 0, rgba(255,255,255,.9) 6%, rgba(255,255,255,0) 26%),
    radial-gradient(circle at 36% 30%, color-mix(in srgb, var(--c,#ccc) 72%, #fff) 0, var(--c,#ccc) 48%, color-mix(in srgb, var(--c,#ccc) 58%, #000) 100%);
  box-shadow: inset -3px -4px 7px rgba(0,0,0,.32), inset 1px 1px 3px rgba(255,255,255,.6), 0 0 0 1px rgba(0,0,0,.12), 0 6px 9px -3px rgba(0,0,0,.4); }
.sh-dot i::after{ content:""; position:absolute; right:19%; bottom:15%; width:26%; height:14%; border-radius:50%; background:rgba(255,255,255,.38); transform:rotate(-38deg); filter:blur(.5px); }
.sh-dot.multi i{ background:
    radial-gradient(circle at 30% 24%, #fff 0, rgba(255,255,255,.9) 6%, rgba(255,255,255,0) 26%),
    radial-gradient(circle at 50% 50%, rgba(255,255,255,0) 55%, rgba(0,0,0,.28) 100%),
    conic-gradient(#ff5c5c, #ffb347, #ffe94d, #5ddc6c, #4dd8ff, #6d7cff, #c86bff, #ff5c5c); }
.sh-dot:hover{ transform:translateY(-3px) scale(1.08); border-color:${YEL}; }
.sh-dot:active{ transform:scale(.94); }
.sh-dot.on{ border-color:${INK}; }
.sh-dot.gone{ opacity:.4; }
.sh-sizes{ display:flex; flex-wrap:wrap; gap:8px; }
.sh-size{ min-width:46px; height:40px; padding:0 14px; border-radius:999px; border:1px solid ${INK}; background:linear-gradient(180deg,#fff,#f0f0f0); box-shadow:inset 0 2px 1px #fff, 0 3px 0 ${INK}; font-size:12px; font-weight:500; letter-spacing:.1em; text-transform:uppercase; transition:all .2s; }
.sh-size:hover:not(:disabled){ background:${YEL_GLOSS}; color:${INK}; transform:translateY(-1px); }
.sh-size.on{ background:${INK}; color:#fff; border-color:${INK}; }
.sh-size.on:hover:not(:disabled){ background:${YEL_GLOSS}; color:${INK}; }
.sh-size:disabled{ border-color:#D4D4D4; color:#A3A3A3; text-decoration:line-through; opacity:1; box-shadow:none; }
.sh-total{ display:flex; align-items:baseline; justify-content:space-between; margin-top:20px; padding-top:14px; border-top:1px solid ${LINE}; }
.sh-total small{ font-size:10px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; color:${BODY}; }
.sh-total b{ font-family:'Cormorant Garamond',serif; font-size:30px; font-weight:600; }
.sh-buyrow{ display:flex; align-items:center; gap:12px; margin-top:12px; }
.sh-qty{ display:inline-flex; align-items:center; height:48px; border:1px solid ${INK}; border-radius:999px; overflow:hidden; }
.sh-qty button{ width:42px; height:100%; display:flex; align-items:center; justify-content:center; transition:background .2s; }
.sh-qty button:hover:not(:disabled){ background:${YEL}; }
.sh-qty b{ min-width:26px; text-align:center; font-weight:500; font-size:15px; }
.sh-actions{ display:flex; flex-direction:column; gap:10px; margin-top:12px; }

.sh-lb{ position:fixed; inset:0; z-index:900; background:rgba(0,0,0,.92); display:flex; align-items:center; justify-content:center; padding:24px; }
.sh-lb img{ max-width:min(92vw,820px); max-height:82vh; object-fit:contain; border-radius:14px; }
.sh-lb-btn{ position:fixed; width:46px; height:46px; border-radius:50%; background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.5); color:#fff !important; display:flex; align-items:center; justify-content:center; z-index:901; transition:all .2s; }
.sh-lb-btn:hover{ background:${YEL}; border-color:${YEL}; color:${INK} !important; }
.sh-lb-btn.l{ left:18px; top:50%; transform:translateY(-50%); } .sh-lb-btn.r{ right:18px; top:50%; transform:translateY(-50%); } .sh-lb-btn.x{ top:18px; right:18px; }

.sh-filter{ padding:26px 22px 24px; }
.sh-filter h3{ font-family:'Cormorant Garamond',serif; font-size:34px; font-weight:600; margin:0 0 6px; padding-right:48px; }
.sh-filter p{ margin:0 0 14px; color:${BODY}; font-size:14px; line-height:1.5; }
.sh-flabel{ display:block; font-size:11px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; margin:22px 0 10px; }
.sh-fwrap{ display:flex; flex-wrap:wrap; gap:8px; }
.sh-prices{ display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.sh-input{ width:100%; height:46px; padding:0 14px; border-radius:12px; border:1px solid #D4D4D4; background:#fff; font-size:15px; color:${INK}; outline:none; transition:border-color .2s, box-shadow .2s; }
.sh-input:focus{ border-color:${INK}; box-shadow:0 0 0 3px ${YEL}; }
.sh-switch{ display:flex; align-items:center; gap:12px; width:100%; padding:11px 14px; border-radius:14px; border:1px solid #D4D4D4; text-align:left; margin-top:10px; transition:all .2s; }
.sh-switch:hover{ border-color:${INK}; background:${YEL_SOFT}; }
.sh-switch b{ font-size:14px; font-weight:500; }
.sh-knob{ position:relative; width:44px; height:26px; border-radius:999px; background:#CFCFCF; flex-shrink:0; transition:background .2s; }
.sh-knob::after{ content:""; position:absolute; top:3px; left:3px; width:20px; height:20px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.3); transition:transform .2s; }
.sh-switch.on .sh-knob{ background:${INK}; }
.sh-switch.on .sh-knob::after{ transform:translateX(18px); }
.sh-ffoot{ display:grid; grid-template-columns:1fr 1.4fr; gap:10px; margin-top:26px; }
.sh-ffoot.one{ grid-template-columns:1fr; }
.sh-helpbtns{ display:flex; flex-direction:column; gap:12px; margin-top:18px; }

@media (max-width:768px){
  .sh-catbtn{ width:48px; padding:0; }
  .sh-catbtn span{ display:none; }
}
@media (prefers-reduced-motion: reduce){
  .sh-scrim, .sh-panel, .sh-dock, .sh-toast{ animation:none !important; }
  .sh-page *, .sh-portal *{ transition:none !important; }
  .sh-card::after{ display:none; }
}
${HOVER_MIX}
`;

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= breakpoint : false);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return mobile;
}

/* ═══════════════════════════ OVERLAY ═══════════════════════════ */
const Overlay: React.FC<{ label: string; onClose: () => void; wide?: boolean; children: React.ReactNode }> = ({ label, onClose, wide, children }) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  return createPortal(
    <div className={`sh-portal sh-scrim ${isMobile ? "mobile" : "desktop"}`} onClick={onClose}>
      <div
        role="dialog" aria-modal="true" aria-label={label}
        className={`sh-panel ${isMobile ? "mobile" : "desktop"}${wide ? " wide" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {isMobile && <div className="sh-handle" />}
        <button type="button" className="sh-x" aria-label="Close" onClick={onClose}><X size={18} strokeWidth={1.8} /></button>
        <div className="sh-scroll">{children}</div>
      </div>
    </div>,
    document.body
  );
};

/* ═══════════════════════════ PRODUCT DETAIL (own component so its state never resets) ═══════════════════════════ */
const Detail: React.FC<{
  p: Product;
  inBag: number;
  onAdd: (qty: number, pick?: PickedOpt) => void;
  onBuy: (qty: number, pick?: PickedOpt) => void;
  onInc: (pick?: PickedOpt) => void;
  onRemove: () => void;
}> = ({ p, inBag, onAdd, onBuy, onInc, onRemove }) => {
  const [idx, setIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [lightbox, setLightbox] = useState(false);
  const touchX = useRef(0);

  const variants = variantsOf(p);
  const [color, setColor] = useState<string | null>(() => (variants.find((v) => variantQty(v) > 0) ?? variants[0])?.color ?? null);
  const [size, setSize] = useState<string | null>(null);
  const cur = variants.find((v) => v.color === color);
  const mainImgs: string[] = (p as any).images ?? [];
  /* the chosen color's photos; otherwise main photos; otherwise any color's photos */
  const imgs: string[] = cur?.images?.length ? cur.images : mainImgs.length ? mainImgs : variants.flatMap((v) => v.images || []);
  const sizeRows = (cur?.stock || []).filter((s) => s.size !== "-");
  const needsSize = sizeRows.length > 0;
  const needPick = needsSize && !size;
  const pickQty = !cur ? Infinity : needsSize ? (size ? Number(sizeRows.find((s) => s.size === size)?.qty) || 0 : Infinity) : variantQty(cur);
  const pick: PickedOpt = { color: cur?.color, size: size ?? undefined };
  const stock = stockOf(p);
  const price = Number((p as any).price) || 0;
  const room = Math.max(0, Math.min(stock - inBag, pickQty));   // capped by the chosen color and size
  const maxed = room <= 0;
  const q = Math.min(qty, Math.max(1, room));
  const catId = idOf((p as any).subcategoryId) || idOf((p as any).categoryId);
  const crumb = catPathLabel(catId);
  const specs = specsOf(p);
  const where = [(p as any).city, (p as any).country].filter(Boolean).join(", ");

  const step = (d: number) => setQty(Math.min(Math.max(1, q + d), Math.max(1, room)));
  const go = (d: number) => { if (imgs.length) setIdx((i) => (i + d + imgs.length) % imgs.length); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && lightbox) { e.stopPropagation(); setLightbox(false); return; }
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, imgs.length]);

  return (
    <div className="sh-detail">
      <div className="sh-gal">
        <div className={`sh-gal-main${imgs[idx] ? " zoom" : ""}`} onClick={() => imgs[idx] && setLightbox(true)}>
          {imgs[idx] ? <img src={imgs[idx]} alt={p.name} /> : <PackageOpen size={46} strokeWidth={1.1} />}
          {imgs.length > 1 && (
            <>
              <button type="button" className="sh-nav l" aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); go(-1); }}><ChevronLeft size={18} /></button>
              <button type="button" className="sh-nav r" aria-label="Next photo" onClick={(e) => { e.stopPropagation(); go(1); }}><ChevronRight size={18} /></button>
            </>
          )}
        </div>
        {imgs.length > 1 && (
          <div className="sh-thumbs">
            {imgs.map((u, i) => (
              <button type="button" key={u + i} className={`sh-thumb ${i === idx ? "on" : ""}`} aria-label={`Photo ${i + 1}`} onClick={() => setIdx(i)}>
                <img src={u} alt="" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="sh-side">
        {crumb && <span className="sh-crumb">{crumb}</span>}
        <h2 className="sh-pname">{p.name}</h2>
        <span className="sh-pprice">{money(price)}</span>

        {(specs.length > 0 || where || inBag > 0) && (
          <div className="sh-meta">
            {specs.map(([k, v]) => <div key={k}><small>{k}</small><span>{v}</span></div>)}
            {where && <div><small>Location</small><span>{where}</span></div>}
            {inBag > 0 && <div><small>In your cart</small><span>{inBag}</span></div>}
          </div>
        )}

        {variants.length > 0 && (
          <div className="sh-opt">
            <small>Color{cur ? <>: <b>{cur.color}</b></> : null}</small>
            <div className="sh-colors">
              {variants.map((v) => (
                <button
                  type="button" key={v.color} aria-label={v.color} aria-pressed={v.color === color}
                  className={`sh-dot${v.color === color ? " on" : ""}${v.hex ? "" : " multi"}${variantQty(v) <= 0 ? " gone" : ""}`}
                  onClick={() => { setColor(v.color); setSize(null); setIdx(0); setQty(1); }}
                >
                  <i style={v.hex ? ({ "--c": v.hex } as React.CSSProperties) : undefined} />
                </button>
              ))}
            </div>
          </div>
        )}

        {needsSize && (
          <div className="sh-opt">
            <small>Size{size ? <>: <b>{size}</b></> : null}</small>
            <div className="sh-sizes">
              {sizeRows.map((s) => (
                <button
                  type="button" key={s.size} className={`sh-size${size === s.size ? " on" : ""}`}
                  disabled={(Number(s.qty) || 0) <= 0} aria-pressed={size === s.size}
                  onClick={() => { setSize(size === s.size ? null : s.size); setQty(1); }}
                >
                  {s.size}
                </button>
              ))}
            </div>
          </div>
        )}

        <span className={`sh-stock ${stock <= 5 ? "low" : ""}`}>{stock <= 5 ? `Only ${stock} left` : "In stock"}</span>
        {maxed && <p className="sh-note">You already have all {stock} in your cart</p>}

        <div className="sh-total">
          <small>{inBag > 0 ? "In your cart" : "Total"}</small>
          <b>{money(price * (inBag > 0 ? inBag : q))}</b>
        </div>

        <div className="sh-buyrow">
          {inBag > 0 ? (
            /* already in cart: the stepper edits the cart itself */
            <div className="sh-qty">
              <button type="button" aria-label="Remove one from cart" onClick={onRemove}><Minus size={16} /></button>
              <b>{inBag}</b>
              <button type="button" aria-label="Add one to cart" disabled={maxed || needPick} onClick={() => onInc(pick)}><Plus size={16} /></button>
            </div>
          ) : (
            <>
              <div className="sh-qty">
                <button type="button" aria-label="Less" disabled={maxed || q <= 1} onClick={() => step(-1)}><Minus size={16} /></button>
                <b>{q}</b>
                <button type="button" aria-label="More" disabled={maxed || q >= room} onClick={() => step(1)}><Plus size={16} /></button>
              </div>
              <button type="button" className="sh-btn block" disabled={maxed || needPick} onClick={() => onAdd(q, pick)}>
                <ShoppingBag size={16} /> {needPick ? "Choose a size" : "Add to cart"}
              </button>
            </>
          )}
        </div>

        <div className="sh-actions">
          <button
            type="button" className="sh-btn ghost block"
            disabled={stock <= 0 || (inBag === 0 && needPick)}
            onClick={() => (inBag > 0 ? onBuy(0, pick) : onBuy(q, pick))}
          >
            {inBag > 0 ? "Go to checkout" : "Buy now"} <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {lightbox && imgs.length > 0 && (
            <motion.div
              className="sh-portal sh-lb" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setLightbox(false)}
              onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - touchX.current; if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); }}
            >
              <button type="button" className="sh-lb-btn x" aria-label="Close" onClick={(e) => { e.stopPropagation(); setLightbox(false); }}><X size={20} /></button>
              {imgs.length > 1 && (
                <>
                  <button type="button" className="sh-lb-btn l" aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); go(-1); }}><ChevronLeft size={24} /></button>
                  <button type="button" className="sh-lb-btn r" aria-label="Next photo" onClick={(e) => { e.stopPropagation(); go(1); }}><ChevronRight size={24} /></button>
                </>
              )}
              <motion.img
                key={idx} src={imgs[idx]} alt={p.name}
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.18 }}
                onClick={(e) => e.stopPropagation()}
              />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

/* ═══════════════════════════ SHOP ═══════════════════════════ */
export default function Shop() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useIsMobile();

  const authUser = useSelector((s: RootState) => s.auth.user) as any;
  const userId: string = authUser?.userId ?? authUser?.uid ?? authUser?.id ?? "";
  const allProducts = useSelector((s: any) => s.products?.products) as Product[] | undefined;
  const cartItems = useSelector(selectCartItems);

  const feedRef = useRef<HTMLDivElement>(null);

  const [q, setQ] = useState("");
  const [activeDept, setActiveDept] = useState<string | null>(null);
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);
  const [openNodes, setOpenNodes] = useState<Set<string>>(() =>
    typeof window !== "undefined" && window.innerWidth > 768
      ? new Set<string>(AllCategoriesFlat.filter((c) => c.parentId && childrenOf(c.id).length > 0).map((c) => c.id))
      : new Set<string>()
  );
  const [tilesOpen, setTilesOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(typeof window !== "undefined" && window.innerWidth > 768);          // categories panel: sidebar on desktop, bottom sheet on mobile
  const [menuOpen, setMenuOpen] = useState(false);
  const [sort, setSort] = useState<Sort>("new");
  const [feed, setFeed] = useState<Feed>("home");
  const [savedOnly, setSavedOnly] = useState(false);
  const [minP, setMinP] = useState("");
  const [maxP, setMaxP] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [mSearch, setMSearch] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [favs, setFavs] = useState<Set<string>>(() => {
    try { return new Set<string>(JSON.parse(localStorage.getItem(FAV_KEY) || "[]")); } catch { return new Set<string>(); }
  });

  /* ── listeners / chrome ── */
  useEffect(() => {
    const unsub = dispatch(listenToPublishedProducts() as any) as any;
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch]);

  useEffect(() => {
    try { localStorage.setItem(FAV_KEY, JSON.stringify(Array.from(favs))); } catch { /* storage unavailable */ }
  }, [favs]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 1200);
    return () => clearTimeout(t);
  }, [flash]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuOpen]);

  /* the mobile feed jumps back to the first slide whenever the list changes */
  useEffect(() => {
    feedRef.current?.scrollTo({ top: 0 });
  }, [feed, activeCat, activeDeep, q, sort, savedOnly, minP, maxP]);

  /* ── data: only published, in-stock products ── */
  const visible = useMemo(
    () =>
      (allProducts ?? [])
        .filter((p) => isLive(p) && stockOf(p) > 0)
        .map((p) => ({
          p,
          chain: new Set<string>([
            ...chainIdsOf(idOf((p as any).categoryId)),
            ...chainIdsOf(idOf((p as any).subcategoryId)),
          ]),
        })),
    [allProducts]
  );

  /* product count per category node: shown beside every category, even the empty ones */
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    visible.forEach((v) => v.chain.forEach((id) => m.set(id, (m.get(id) || 0) + 1)));
    return m;
  }, [visible]);

  /* ALL categories are visible, whether or not they hold products yet */
  const visibleChildren = (parentId: string) => childrenOf(parentId);

  const depts = DEPARTMENTS;

  const topCats = useMemo(
    () => (activeDept ? [activeDept] : depts.map((d) => d.id)).flatMap((id) => childrenOf(id)),
    [activeDept, depts]
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const min = minP === "" ? null : Number(minP);
    const max = maxP === "" ? null : Number(maxP);
    const priceOf = (p: Product) => Number((p as any).price) || 0;
    const newestFirst = (a: Product, b: Product) => ((b as any).createdAt ?? 0) - ((a as any).createdAt ?? 0);

    let list = visible.filter(({ p, chain }) => {
      if (activeDept && !chain.has(activeDept)) return false;
      if (activeCat && !chain.has(activeCat)) return false;
      if (activeDeep && !chain.has(activeDeep)) return false;
      if (savedOnly && !favs.has(p.productId)) return false;
      if (min !== null && priceOf(p) < min) return false;
      if (max !== null && priceOf(p) > max) return false;
      if (term) {
        const hay = `${p.name ?? ""} ${(p as any).description ?? ""} ${catPathLabel(idOf((p as any).categoryId))} ${Object.values(attrsOf(p)).flat().join(" ")}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    }).map((v) => v.p);

    if (sort === "low") list = [...list].sort((a, b) => priceOf(a) - priceOf(b));
    else if (sort === "high") list = [...list].sort((a, b) => priceOf(b) - priceOf(a));
    else if (sort === "name") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else list = [...list].sort(newestFirst);

    /* mobile New Arrivals tab = latest 30 */
    if (feed === "new") list = [...list].sort(newestFirst).slice(0, 30);
    return list;
  }, [visible, q, activeDept, activeCat, activeDeep, sort, savedOnly, minP, maxP, favs, feed]);

  const activeFilters = [sort !== "new", minP !== "", maxP !== "", savedOnly].filter(Boolean).length;
  const hasFilters = !!(q || activeDept || activeCat || activeDeep || activeFilters || feed !== "home");
  const openProduct = openId ? (allProducts ?? []).find((p) => p.productId === openId) : undefined;
  const activeLabel = catPathLabel(activeDeep ?? activeCat);

  const clearAll = () => {
    setQ(""); setActiveDept(null); setActiveCat(null); setActiveDeep(null); setSort("new"); setSavedOnly(false); setMinP(""); setMaxP(""); setFeed("home");
  };

  /* ── category selection ── */
  const selectNode = (id: string) => {
    const chain = chainIdsOf(id);                 // [dept, category, type?, sub?]
    const cat = chain[1] ?? null;
    if (chain.length < 2) return;
    if (chain.length === 2) {                     // a category
      if (activeCat === id && !activeDeep) setActiveCat(null);
      else { setActiveCat(id); setActiveDeep(null); }
      return;
    }
    if (activeDeep === id) {                      // tap the picked type/sub again: step back up one level
      const parent = chain[chain.length - 2];
      setActiveDeep(parent === cat ? null : parent);
      return;
    }
    setActiveCat(cat);
    setActiveDeep(id);
  };

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const toggleFav = (id: string) =>
    setFavs((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  /* ── cart ── */
  const cartCount = cartItems.reduce((s: number, i: any) => s + lineQty(i), 0);
  const inBagOf = (productId: string) =>
    cartItems.filter((i: any) => lineId(i) === productId).reduce((s: number, i: any) => s + lineQty(i), 0);

  const buildLine = (p: Product, quantity: number, pick?: PickedOpt) => {
    const v = pick?.color ? variantsOf(p).find((x) => x.color === pick.color) : undefined;
    return {
      productId: p.productId,
      name: p.name,
      price: Number((p as any).price) || 0,
      quantity,
      photo: v?.images?.[0] || coverOf(p) || "",
      ownerId: (p as any).ownerId ?? "",
      ...(pick?.color ? { color: pick.color } : {}),
      ...(pick?.size ? { size: pick.size } : {}),
    };
  };

  const addLine = (p: Product, quantity: number, pick?: PickedOpt) => dispatch(addToCart(buildLine(p, quantity, pick) as any) as any);

  const goToCheckout = (items: any[] = cartItems) => {
    if (!items.length) { setToast("Your cart is empty"); return; }   // nothing in the cart: stay here
    navigate("/checkout", { state: { items } });
  };

  const add = (p: Product, qty = 1, pick?: PickedOpt): boolean => {
    const room = stockOf(p) - inBagOf(p.productId);
    if (room <= 0) { setToast("All available pieces are in your cart"); return false; }
    addLine(p, Math.min(qty, room), pick);
    setFlash(p.productId);
    setToast(`${p.name} added to cart`);
    return true;
  };

  const buy = (p: Product, qty: number, pick?: PickedOpt) => {
    setOpenId(null);
    const room = stockOf(p) - inBagOf(p.productId);
    if (qty <= 0 || room <= 0) { goToCheckout(); return; }
    const n = Math.min(qty, room);
    const line = buildLine(p, n, pick);
    addLine(p, n, pick);
    // the store hasn't re-rendered yet, so hand checkout the cart including this line
    const idx = cartItems.findIndex((i: any) => lineId(i) === line.productId);
    const next = idx < 0
      ? [...cartItems, line]
      : cartItems.map((i: any, k: number) => (k === idx ? { ...i, quantity: lineQty(i) + n } : i));
    goToCheckout(next);
  };

  const handleLogout = async () => {
    await dispatch(logout() as any);
    navigate("/auth");
  };

  const quickAdd = (p: Product) => {
    const vs = variantsOf(p);
    const v = vs.find((x) => variantQty(x) > 0) ?? vs[0];
    const sz = (v?.stock || []).find((s) => s.size !== "-" && Number(s.qty) > 0)?.size;
    add(p, 1, v ? { color: v.color, size: sz } : undefined);
  };

  const shareProduct = async (p: Product) => {
    const url = `${window.location.origin}/shop`;
    try {
      if ((navigator as any).share) await (navigator as any).share({ title: p.name, url });
      else { await navigator.clipboard.writeText(url); setToast("Link copied"); }
    } catch { /* share cancelled */ }
  };

  /* ═══════════════════════ render pieces ═══════════════════════ */
  const renderNode = (node: CategoryItem, depth: number): React.ReactNode => {
    const kids = visibleChildren(node.id);
    const open = openNodes.has(node.id);
    const exact = activeDeep ?? activeCat;
    const picked = exact === node.id;
    const trail = chainIdsOf(exact).includes(node.id);
    const n = counts.get(node.id) || 0;
    return (
      <div key={node.id}>
        <button
          type="button"
          className={`sh-node${picked ? " on" : trail ? " trail" : ""}`}
          onClick={() => {
            selectNode(node.id);
            if (kids.length) toggleNode(node.id);
            else if (isMobile) setCatOpen(false);
          }}
        >
          <span className="sh-node-ic">{node.icon}</span>
          <span className="sh-node-name">{node.name}</span>
          <span className={`sh-node-ct${n === 0 ? " zero" : ""}`}>{n}</span>
          {kids.length > 0 ? (
            <motion.span className="sh-node-chev" animate={{ rotate: open ? 90 : 0 }}><ChevronRight size={15} /></motion.span>
          ) : picked ? <Check size={15} /> : null}
        </button>
        <AnimatePresence initial={false}>
          {open && kids.length > 0 && (
            <motion.div key="kids" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div className="sh-node-kids">{kids.map((k) => renderNode(k, depth + 1))}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const renderTree = () => (
    <>
      <button
        type="button"
        className={`sh-node sh-node-all${!activeCat && !activeDeep ? " on" : ""}`}
        onClick={() => { setActiveCat(null); setActiveDeep(null); if (isMobile) setCatOpen(false); }}
      >
        <span className="sh-node-ic"><NineDots size={14} /></span>
        <span className="sh-node-name">All products</span>
        <span className="sh-node-ct">{visible.length}</span>
        {!activeCat && !activeDeep && <Check size={15} />}
      </button>
      {topCats.length === 0 ? (
        <p style={{ fontSize: 13, color: BODY, padding: "12px 8px", margin: 0 }}>No categories yet.</p>
      ) : (
        topCats.map((c) => renderNode(c, 0))
      )}
    </>
  );

  const renderStrip = () => {
    if (topCats.length === 0) return null;
    return (
      <div className="sh-strip">
        <button type="button" className={`sh-sc${!activeCat ? " on" : ""}`} onClick={() => { setActiveCat(null); setActiveDeep(null); }}>
          <span className="sh-orb"><NineDots size={24} /></span>
          <span className="sh-sc-l">All</span>
        </button>
        {topCats.map((c) => (
          <button type="button" key={c.id} className={`sh-sc${activeCat === c.id ? " on" : ""}`} onClick={() => selectNode(c.id)}>
            <span className="sh-orb">
              {(c as any).image ? <img src={(c as any).image} alt={c.name} /> : c.icon}
            </span>
            <span className="sh-sc-l">{c.name}</span>
          </button>
        ))}
      </div>
    );
  };

  const renderTiles = () => {
    if (!activeCat || !tilesOpen) return null;
    const row1 = visibleChildren(activeCat);
    if (!row1.length) return null;
    const typeSel = chainIdsOf(activeDeep).find((id) => row1.some((t) => t.id === id)) ?? null;
    const row2 = typeSel ? visibleChildren(typeSel) : [];

    const tile = (n: CategoryItem, parentId: string | null) => (
      <button
        type="button" key={n.id}
        className={`sh-tile${activeDeep === n.id ? " on" : ""}`}
        onClick={() => setActiveDeep(activeDeep === n.id ? parentId : n.id)}
      >
        <span className="sh-tile-ic">{n.icon}</span>
        <span className="sh-tile-n">{n.name}</span>
      </button>
    );

    return (
      <div>
        <AnimatePresence initial={false}>
          {tilesOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div className="sh-tiles">{row1.map((n) => tile(n, null))}</div>
              {row2.length > 0 && <div className="sh-tiles">{row2.map((n) => tile(n, typeSel))}</div>}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* desktop: square glossy card */
  const renderCard = (p: Product) => {
    const stock = stockOf(p);
    const fav = favs.has(p.productId);
    const inBag = inBagOf(p.productId);
    const cover: string | undefined = coverOf(p);
    const catId = idOf((p as any).subcategoryId) || idOf((p as any).categoryId);
    const label = catName(catId);
    return (
      <div
        key={p.productId} className="sh-card" role="button" tabIndex={0}
        onClick={() => setOpenId(p.productId)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenId(p.productId); } }}
      >
        <div className="sh-media">
          {cover ? <img src={cover} alt={p.name} loading="lazy" draggable={false} /> : <PackageOpen size={36} strokeWidth={1.1} />}
        </div>
        <button
          type="button" className={`sh-fab sh-heart ${fav ? "on" : ""}`} aria-label={fav ? "Remove from saved" : "Save"} aria-pressed={fav}
          onClick={(e) => { e.stopPropagation(); toggleFav(p.productId); }}
        >
          <Heart size={18} fill={fav ? "currentColor" : "none"} strokeWidth={1.8} />
        </button>
        <button
          type="button" className="sh-fab sh-eye" aria-label="View details"
          onClick={(e) => { e.stopPropagation(); setOpenId(p.productId); }}
        >
          <Eye size={18} strokeWidth={1.8} />
        </button>

        {stock <= 5 && <span className="sh-tag">Only {stock} left</span>}
        {inBag > 0 ? (
          <div className="sh-step" onClick={(e) => e.stopPropagation()}>
            <button type="button" aria-label="Remove one" onClick={() => dispatch(decrementCartItem({ productId: p.productId } as any) as any)}><Minus size={14} /></button>
            <b>{inBag}</b>
            <button type="button" aria-label="Add one" disabled={inBag >= stock} onClick={() => quickAdd(p)}><Plus size={14} /></button>
          </div>
        ) : (
          <button
            type="button" className="sh-fab sh-cart" aria-label={`Add ${p.name} to cart`}
            onClick={(e) => { e.stopPropagation(); quickAdd(p); }}
          >
            {flash === p.productId ? <Check size={18} strokeWidth={2.2} /> : (
              <>
                <ShoppingBag size={18} strokeWidth={1.8} />
                <Plus className="sh-plus" size={12} strokeWidth={3.4} />
              </>
            )}
          </button>
        )}

        <div className="sh-info">
          <b className="sh-name">{label || p.name.split(/\s+/).slice(0, 3).join(" ")}</b>
          <span className="sh-price">{money(Number((p as any).price) || 0)}</span>
        </div>
      </div>
    );
  };

  /* mobile: full-screen slide, same layout as the JOJO feed */
  const renderSlide = (p: Product) => {
    const fav = favs.has(p.productId);
    const stock = stockOf(p);
    const cover = coverOf(p);
    const catId = idOf((p as any).subcategoryId) || idOf((p as any).categoryId);
    const label = catName(catId) || "Collection";
    const desc = String((p as any).description || "").split("\n")[0].trim();
    const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };
    return (
      <section key={p.productId} className="sh-slide" onClick={() => setOpenId(p.productId)}>
        {cover ? <img className="sh-slide-img" src={cover} alt={p.name} loading="lazy" draggable={false} /> : <div className="sh-slide-ph"><PackageOpen size={64} strokeWidth={1} /></div>}
        <div className="sh-slide-shade" />

        <div className="sh-vrail">
          <div className="sh-vact">
            <button type="button" className={`sh-vbtn${fav ? " on" : ""}`} aria-label={fav ? "Remove from saved" : "Like"} aria-pressed={fav} onClick={stop(() => toggleFav(p.productId))}>
              <Heart size={28} fill={fav ? "currentColor" : "none"} strokeWidth={1.8} />
            </button>
            <span>{fav ? "Liked" : "Like"}</span>
          </div>
          <div className="sh-vact">
            <button type="button" className="sh-vbtn dark" aria-label="View details" onClick={stop(() => setOpenId(p.productId))}>
              <Eye size={28} strokeWidth={1.8} />
            </button>
            <span>View</span>
          </div>
          <div className="sh-vact">
            <button type="button" className="sh-vbtn dark" aria-label="Share" onClick={stop(() => shareProduct(p))}>
              <Share2 size={26} strokeWidth={1.8} />
            </button>
            <span>Share</span>
          </div>
          <div className="sh-vact">
            <button type="button" className="sh-vbtn buy" aria-label={`Buy ${p.name}`} onClick={stop(() => setOpenId(p.productId))}>
              {flash === p.productId ? <Check size={28} strokeWidth={2.2} /> : <ShoppingBag size={28} strokeWidth={1.8} />}
            </button>
            <span>Buy Now</span>
          </div>
        </div>

        <div className="sh-vinfo">
          <div className="sh-vcat">
            <span>{label}</span>
            {stock <= 5 && <span className="sh-vtag">Only {stock} left</span>}
          </div>
          <h3 className="sh-vname">{p.name}</h3>
          {desc && <p className="sh-vdesc">{desc}</p>}
          <div className="sh-vprice">{money(Number((p as any).price) || 0)}</div>
        </div>
      </section>
    );
  };

  const desktopTabs = [
    { key: "home", label: "Home", Icon: HomeIcon, on: true, go: () => navigate("/") },
    { key: "cart", label: "Cart", Icon: ShoppingBag, on: false, go: () => goToCheckout() },
    { key: "orders", label: "Orders", Icon: ClipboardList, on: false, go: () => navigate("/orders") },
    { key: "profile", label: "Profile", Icon: User, on: false, go: () => navigate(`/profile/${userId}`) },
  ];

  const mobileTabs: { key: string; label: string; Icon?: React.ComponentType<any>; on: boolean; center?: boolean; go: () => void }[] = [
    { key: "home", label: "Home", Icon: HomeIcon, on: feed === "home", go: () => { setFeed("home"); feedRef.current?.scrollTo({ top: 0, behavior: "smooth" }); } },
    { key: "orders", label: "Orders", Icon: ClipboardList, on: false, go: () => navigate("/orders") },
    { key: "browse", label: "Browse", on: false, center: true, go: () => setCatOpen(true) },
    { key: "new", label: "New Arrivals", Icon: Sparkles, on: feed === "new", go: () => setFeed("new") },
    { key: "profile", label: "Profile", Icon: User, on: false, go: () => navigate(`/profile/${userId}`) },
  ];

  const loading = allProducts === undefined;

  return (
    <div className={`sh-page${isMobile ? " m" : ""}`}>
      <style>{CSS}</style>

      {isMobile ? (
        <>
          {/* ── JOJO-style transparent header ── */}
          <header className="sh-mtop">
            <div className="sh-mtop-in">
              <img className="sh-logo" src={logo} alt="Logo" onClick={() => navigate("/")} />
              <span className="sh-cur"><Globe size={22} strokeWidth={1.6} /> {CURRENCY_LABEL}</span>
              <button type="button" className="sh-mcirc" aria-label="Search" onClick={() => setMSearch((v) => !v)}><Search size={20} strokeWidth={1.8} /></button>
              <button type="button" className="sh-mcirc" aria-label="Open cart" onClick={() => goToCheckout()}>
                <ShoppingBag size={20} strokeWidth={1.8} />
                {cartCount > 0 && <i className="sh-badge">{cartCount > 99 ? "99+" : cartCount}</i>}
              </button>
              <button type="button" className="sh-mcirc" aria-label="Account" onClick={() => navigate(`/profile/${userId}`)}><User size={20} strokeWidth={1.8} /></button>
              <div onClick={(e) => e.stopPropagation()}>
                <button type="button" className="sh-mcirc" aria-label="Menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)}>
                  {menuOpen ? <X size={20} strokeWidth={1.8} /> : <MenuIcon size={20} strokeWidth={1.8} />}
                </button>
                <AnimatePresence>
                  {menuOpen && (
                    <motion.div className="sh-menu" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                      <button type="button" onClick={() => { setMenuOpen(false); setFilterOpen(true); }}><SlidersHorizontal size={15} /> Filters{activeFilters > 0 ? ` (${activeFilters})` : ""}</button>
                      <button type="button" onClick={() => navigate("/orders")}><ClipboardList size={15} /> Orders</button>
                      <button type="button" onClick={() => navigate("/owner")}><LayoutDashboard size={15} /> Seller dashboard</button>
                      <button type="button" onClick={handleLogout}><LogOut size={15} /> Sign out</button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </header>

          {(mSearch || q || activeLabel || savedOnly) && (
            <div className="sh-mbar">
              {(mSearch || q) && (
                <label className="sh-msearch">
                  <Search size={18} strokeWidth={1.8} />
                  <input autoFocus={mSearch && !q} placeholder="Search the collection" value={q} onChange={(e) => setQ(e.target.value)} />
                  {q && <button type="button" aria-label="Clear search" onClick={() => setQ("")}><X size={16} /></button>}
                </label>
              )}
              {(activeLabel || savedOnly) && (
                <span className="sh-mpill">
                  <span>{[activeLabel, savedOnly ? "Saved" : ""].filter(Boolean).join(" · ")}</span>
                  <button type="button" aria-label="Clear category" onClick={() => { setActiveCat(null); setActiveDeep(null); setSavedOnly(false); }}><X size={12} strokeWidth={2.6} /></button>
                </span>
              )}
            </div>
          )}

          {/* ── vertical snap feed ── */}
          <div className="sh-feed" ref={feedRef}>
            {loading ? (
              <div className="sh-vempty"><PackageOpen size={52} strokeWidth={1.1} /><h3>Loading the collection</h3></div>
            ) : filtered.length === 0 ? (
              <div className="sh-vempty">
                <PackageOpen size={52} strokeWidth={1.1} />
                <h3>{visible.length ? "No pieces match" : "Nothing here yet"}</h3>
                <p>{visible.length ? "Try a different category or filter." : "New pieces are on the way."}</p>
                {hasFilters && <button type="button" className="sh-btn" onClick={clearAll}>Clear filters</button>}
              </div>
            ) : (
              filtered.map(renderSlide)
            )}
          </div>

          {/* ── JOJO-style bottom bar ── */}
          <nav className="sh-mdock" aria-label="Main">
            {mobileTabs.map(({ key, label, Icon, on, center, go }) =>
              center ? (
                <button type="button" key={key} className="sh-mtab center" onClick={go} aria-label={label}>
                  <span className="sh-browse"><NineDots size={26} /></span>
                  <span>{label}</span>
                </button>
              ) : (
                <button type="button" key={key} className={`sh-mtab${on ? " on" : ""}`} onClick={go} aria-current={on ? "page" : undefined}>
                  <span className="ic">{Icon && <Icon size={26} strokeWidth={1.7} />}</span>
                  <span>{label}</span>
                </button>
              )
            )}
          </nav>
        </>
      ) : (
        <>
          <header className="sh-top">
            <div className="sh-top-in">
              <button type="button" className={`sh-catbtn${catOpen ? " on" : ""}`} onClick={() => setCatOpen((v) => !v)} aria-label="Categories" aria-expanded={catOpen}>
                <NineDots /> <span>Categories</span>
              </button>
              <label className="sh-search">
                <Search size={18} strokeWidth={1.8} />
                <input placeholder="Search the collection" value={q} onChange={(e) => setQ(e.target.value)} />
                {q && <button type="button" aria-label="Clear search" onClick={() => setQ("")}><X size={15} /></button>}
              </label>
              <button type="button" className="sh-round" aria-label="Filters" onClick={() => setFilterOpen(true)}>
                <SlidersHorizontal size={19} strokeWidth={1.8} />
                {activeFilters > 0 && <i className="sh-badge">{activeFilters}</i>}
              </button>
              <button type="button" className="sh-round" aria-label="Open cart" onClick={() => goToCheckout()}>
                <ShoppingBag size={19} strokeWidth={1.8} />
                {cartCount > 0 && <i className="sh-badge">{cartCount > 99 ? "99+" : cartCount}</i>}
              </button>
              <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
                <button type="button" className="sh-round" aria-label="Account" aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)}>
                  <User size={19} strokeWidth={1.8} />
                </button>
                <AnimatePresence>
                  {menuOpen && (
                    <motion.div className="sh-menu" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                      <button type="button" onClick={() => navigate(`/profile/${userId}`)}><User size={15} /> Profile</button>
                      <button type="button" onClick={() => navigate("/orders")}><ClipboardList size={15} /> Orders</button>
                      <button type="button" onClick={() => navigate("/owner")}><LayoutDashboard size={15} /> Seller dashboard</button>
                      <button type="button" onClick={handleLogout}><LogOut size={15} /> Sign out</button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </header>

          <div className={`sh-wrap${catOpen ? " wide" : ""}`}>
            <div className={`sh-shell${catOpen ? " side" : ""}`}>
              {catOpen && (
                <aside className="sh-cside" aria-label="Categories">
                  <h4>Categories</h4>
                  {renderTree()}
                </aside>
              )}

              <main className="sh-main">
                {renderStrip()}
                {renderTiles()}

                {loading ? (
                  <div className="sh-empty"><PackageOpen size={46} strokeWidth={1.2} /><h3>Loading the collection</h3></div>
                ) : filtered.length === 0 ? (
                  <div className="sh-empty">
                    <PackageOpen size={46} strokeWidth={1.2} />
                    <h3>{visible.length ? "No pieces match" : "Nothing here yet"}</h3>
                    <p>{visible.length ? "Try a different category or filter." : "New pieces are on the way."}</p>
                    {hasFilters && <button type="button" className="sh-btn ghost" onClick={clearAll}>Clear filters</button>}
                  </div>
                ) : (
                  <div className="sh-grid">{filtered.map(renderCard)}</div>
                )}
              </main>
            </div>
          </div>

          {/* floating footer bar */}
          <nav className="sh-dock" aria-label="Main">
            {desktopTabs.map(({ key, label, Icon, on, go }) => (
              <button type="button" key={key} className={`sh-tab${on ? " on" : ""}`} onClick={go} aria-current={on ? "page" : undefined}>
                <span className="ic">
                  <Icon size={17} strokeWidth={1.8} />
                  {key === "cart" && cartCount > 0 && <i className="sh-badge">{cartCount > 99 ? "99+" : cartCount}</i>}
                </span>
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </>
      )}

      {toast && <div className="sh-toast" role="status">{toast}</div>}

      {openProduct && (
        <Overlay label={openProduct.name} wide onClose={() => setOpenId(null)}>
          <Detail
            key={openProduct.productId}
            p={openProduct}
            inBag={inBagOf(openProduct.productId)}
            onAdd={(n, pick) => { if (add(openProduct, n, pick)) setOpenId(null); }}
            onBuy={(n, pick) => buy(openProduct, n, pick)}
            onInc={(pick) => { add(openProduct, 1, pick); }}
            onRemove={() => dispatch(decrementCartItem({ productId: openProduct.productId } as any) as any)}
          />
        </Overlay>
      )}

      {isMobile && catOpen && (
        <Overlay label="Categories" onClose={() => setCatOpen(false)}>
          <div className="sh-filter">
            <h3>Browse</h3>
            {renderTree()}
            <div className="sh-ffoot one">
              <button type="button" className="sh-btn" onClick={() => setCatOpen(false)}>Show {filtered.length} {filtered.length === 1 ? "piece" : "pieces"}</button>
            </div>
          </div>
        </Overlay>
      )}

      {filterOpen && (
        <Overlay label="Filters" onClose={() => setFilterOpen(false)}>
          <div className="sh-filter">
            <h3>Filters</h3>

            <span className="sh-flabel">Sort by</span>
            <div className="sh-fwrap">
              {SORTS.map((s) => (
                <button type="button" key={s.id} className={`sh-chip ${sort === s.id ? "on" : ""}`} onClick={() => setSort(s.id)}>{s.label}</button>
              ))}
            </div>

            <span className="sh-flabel">Price (€)</span>
            <div className="sh-prices">
              <input className="sh-input" type="number" min="0" inputMode="decimal" placeholder="Min" value={minP} onChange={(e) => setMinP(e.target.value)} />
              <input className="sh-input" type="number" min="0" inputMode="decimal" placeholder="Max" value={maxP} onChange={(e) => setMaxP(e.target.value)} />
            </div>

            <span className="sh-flabel">Show</span>
            <button type="button" className={`sh-switch ${savedOnly ? "on" : ""}`} aria-pressed={savedOnly} onClick={() => setSavedOnly((v) => !v)}>
              <span className="sh-knob" /><b>Saved pieces only</b>
            </button>
            {!isMobile && (
              <button type="button" className={`sh-switch ${tilesOpen ? "on" : ""}`} aria-pressed={tilesOpen} onClick={() => setTilesOpen((v) => !v)}>
                <span className="sh-knob" /><b>Shop by category</b>
              </button>
            )}

            <div className="sh-ffoot">
              <button type="button" className="sh-btn ghost" onClick={clearAll}>Clear all</button>
              <button type="button" className="sh-btn" onClick={() => setFilterOpen(false)}>Show {filtered.length}</button>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
}